import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Enums, Profile } from '@cyborg-monorepo/hunter/common';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subscription,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { environment } from '../../environments/environment';

const REFRESH_STORAGE_KEY = 'hunter-auth-refresh-token';
const ACCESS_STORAGE_KEY = 'hunter-auth-access-token';
const LOGIN_REDIRECT_STORAGE_KEY = 'hunter-login-redirect-path';

export enum LOGOUT_REASON {
  EXPIRED = 'expired',
  REVOKED = 'revoked',
}

export interface PasswordResponse {
  message: string;
}

export interface Tokens {
  accessToken: string;
  refreshToken: string;
}

export type LoginResponse =
  | {
      tokens: Tokens;
      user: Profile;
    }
  | {
      samlLoginUrl: string;
    };

export interface RefreshResponse {
  tokens: Tokens;
}

export interface TokenPayload {
  user: Profile;
  iat: number;
  exp: number;
}

export interface VerityAuth0Tokens {
  accessToken: string;
  idTokenClaims: any
}

@Injectable()
export class AuthService {
  public accessToken$: ReplaySubject<string> = new ReplaySubject(1);
  public user$: BehaviorSubject<Profile | null> = new BehaviorSubject(null);

  private jwtHelper: JwtHelperService;
  private refreshHttp: Observable<Tokens>;
  private refreshTimer: Subscription;

  private readonly defaultLoginRedirectURL: string = '/research';

  public readonly logoutRedirectURL: string = '/login';

  constructor(private httpClient: HttpClient, private router: Router) {
    this.jwtHelper = new JwtHelperService();

    const { accessToken, refreshToken } = this.loadTokens();

    if (refreshToken) {
      if (this.jwtHelper.isTokenExpired(refreshToken)) {
        this.logout(LOGOUT_REASON.EXPIRED);
      }
    }

    if (accessToken) {
      if (!this.jwtHelper.isTokenExpired(accessToken)) {
        this.setTokens({
          refreshToken,
          accessToken,
        });
      } else {
        this.startAutoRefreshTimer();
      }
    }
  }

  /**
   * Checks that the user has the correct tokens to be authenticated.
   */
  public isAuthenticated(): boolean {
    const { refreshToken } = this.loadTokens();
    return !this.jwtHelper.isTokenExpired(refreshToken);
  }

  /**
   * Fetch new refresh and access tokens. Will politely handle multiple calls,
   * but only perform one HTTP request at a time.
   */
  public refreshTokens(): Observable<Tokens> {
    const { refreshToken } = this.loadTokens();

    if (!refreshToken) {
      // if there is no stored refresh token, then we can not get a new refresh
      return throwError(() => new Error('No valid token stored'));
    }

    if (this.jwtHelper.isTokenExpired(refreshToken)) {
      this.logout(LOGOUT_REASON.EXPIRED);
    }

    if (this.refreshHttp == null) {
      this.refreshHttp = this.httpClient
        .post<RefreshResponse>(`${environment.api}/auth/refresh`, {
          refreshToken,
        })
        .pipe(
          tap((resp) => {
            this.clearTokens();
            this.setTokens(resp.tokens);
          }),
          map((resp) => resp.tokens),
          catchError((error) => {
            // Catch failed refresh due to revoked refresh token
            if (error instanceof HttpErrorResponse && error.status === 401) {
              this.logout(LOGOUT_REASON.REVOKED);
            } else {
              return throwError(() => error);
            }
          }),
          finalize(() => {
            this.refreshHttp = null;
          }),
          shareReplay({ bufferSize: 1, refCount: true }),
        );
    }

    return this.refreshHttp;
  }

  /**
   * Saves tokens to session storage and public observables
   * Also starts the timeout countdown for auto logout
   * @param tokens
   */
  public setTokens(tokens: Tokens): void {
    localStorage.setItem(REFRESH_STORAGE_KEY, tokens.refreshToken);
    localStorage.setItem(ACCESS_STORAGE_KEY, tokens.accessToken);

    const decoded: TokenPayload = this.jwtHelper.decodeToken(
      tokens.accessToken,
    );

    this.accessToken$.next(tokens.accessToken);
    this.user$.next(decoded.user);

    this.startAutoRefreshTimer();
  }

  private startAutoRefreshTimer(): void {
    const { accessToken } = this.loadTokens();
    const decoded: TokenPayload = this.jwtHelper.decodeToken(accessToken);

    const expires = (decoded?.exp ?? 0) * 1000;
    const now = new Date().getTime();

    const timerDuration = Math.max((expires - now) / 2, 0);

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }

    this.refreshTimer = timer(timerDuration)
      .pipe(switchMap(() => this.refreshTokens()))
      .subscribe();
  }

  private clearTokens(): void {
    localStorage.removeItem(REFRESH_STORAGE_KEY);
    localStorage.removeItem(ACCESS_STORAGE_KEY);
  }

  public logout(source?: LOGOUT_REASON): void {
    this.clearTokens();

    this.user$.next(null);

    this.setLoginRedirectPath(this.defaultLoginRedirectURL);

    window.location.href = `${this.logoutRedirectURL}${
      source ? '?source=' + source : ''
    }`;
  }

  public login(email: string, password?: string): Observable<LoginResponse> {
    return this.httpClient.post<LoginResponse>(
      `${environment.api}/auth/login`,
      { email, password },
    );
  }

  public auth0SilentLogin(verityAuth0Tokens: VerityAuth0Tokens): Observable<LoginResponse> {
    const { accessToken , idTokenClaims } =  verityAuth0Tokens;

    const headers = new HttpHeaders({ 
      'Authorization': `Bearer ${accessToken}`,
      'Verity-X-ID-Token': JSON.stringify(idTokenClaims)
    });

    try {
      return this.httpClient.get<LoginResponse>(
        `${environment.api}/auth/auth0-silent-login`,
        { headers }
      );
    } 
    catch(err) {
      console.log(err)
    }
  }

  public forgotPassword(email: string): Observable<PasswordResponse> {
    const url = `${environment.api}/auth/forgot-password`;
    return this.httpClient.post<PasswordResponse>(url, { email });
  }

  public resetPassword(
    resetToken: string,
    password: string,
  ): Observable<PasswordResponse> {
    const url = `${environment.api}/auth/reset-password/${resetToken}`;
    return this.httpClient.post<PasswordResponse>(url, { password });
  }

  private loadTokens(): Tokens {
    return {
      accessToken: localStorage.getItem(ACCESS_STORAGE_KEY),
      refreshToken: localStorage.getItem(REFRESH_STORAGE_KEY),
    };
  }

  public setLoginRedirectPath(path: string): void {
    localStorage.setItem(LOGIN_REDIRECT_STORAGE_KEY, path);
  }

  public getLoginRedirectPath(clearStoredPath: boolean = false): string {
    const path =
      localStorage.getItem(LOGIN_REDIRECT_STORAGE_KEY) ??
      this.defaultLoginRedirectURL;

    if (clearStoredPath) {
      localStorage.removeItem(LOGIN_REDIRECT_STORAGE_KEY);
    }

    return path;
  }

  public checkStatus(redirectRoute: string): void {
    // If user is expired, redirect
    this.user$
      .pipe(
        filter((user) => !!user),
        take(1),
      )
      .subscribe((user) => {
        if (user.company.status == Enums.CompanyStatus.EXPIRED) {
          this.router.navigateByUrl(redirectRoute);
          return;
        }
      });
  }
}
